import React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cn } from "../lib/utils";
import { CloseIcon } from "../icons/close";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 bg-gray-4/20 fixed inset-0 z-50",
      className,
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> {
  /**
   * Side of the screen where the sheet should appear.
   */
  side?: "top" | "bottom" | "left" | "right";
  /**
   * Size of the sheet as a percentage of the viewport (0-100).
   * For left/right sides, this represents width (vw).
   * For top/bottom sides, this represents height (vh).
   * If not provided, defaults to predefined sizes.
   */
  size?: number;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", size, className, children, ...props }, ref) => {
  // Separate header and content
  const childrenArray = React.Children.toArray(children);

  // Find the header component
  const headerIndex = childrenArray.findIndex(
    (child) => React.isValidElement(child) && child.type === SheetHeader,
  );

  // Extract header and content elements
  const headerElement = headerIndex !== -1 ? childrenArray[headerIndex] : null;
  const contentElements =
    headerIndex !== -1
      ? childrenArray.filter((_, i) => i !== headerIndex)
      : childrenArray;

  // Generate custom size styles based on the side and size prop
  const customSizeStyles: React.CSSProperties = {};
  if (size !== undefined && size >= 0 && size <= 100) {
    if (side === "left" || side === "right") {
      customSizeStyles.width = `${size}vw`;
    } else if (side === "top" || side === "bottom") {
      customSizeStyles.height = `${size}vh`;
    }
  }

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(
          "fixed z-50 flex max-w-full flex-col overflow-hidden bg-white shadow-lg transition ease-in-out",
          {
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500":
              true,
            "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 max-h-screen":
              side === "top" && !size,
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 max-h-screen":
              side === "bottom" && !size,
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-[500px] max-w-[calc(100vw-6.4%)]":
              side === "left" && !size,
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-[500px] max-w-[calc(100vw-6.4%)]":
              side === "right" && !size,
            // Custom positioning based on side when size is provided
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full":
              side === "left" && size !== undefined,
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full":
              side === "right" && size !== undefined,
            "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 w-full":
              side === "top" && size !== undefined,
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 w-full":
              side === "bottom" && size !== undefined,
          },
          className,
        )}
        style={customSizeStyles}
        {...props}
      >
        <SheetPrimitive.Close className="hover:text-gray-4 text-gray-3 absolute top-6 right-6 z-20 transition-colors hover:cursor-pointer disabled:pointer-events-none lg:top-8.5">
          {/* ensure big enough tap target for touch devices */}
          <span className="absolute h-12 w-12 -translate-x-1/2 -translate-y-1/4 [@media(pointer:fine)]:hidden" />
          <CloseIcon />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>

        {/* Render header if present */}
        {headerElement}

        {/* Content area with overflow scroll */}
        <div className="flex-1 overflow-y-auto p-6 lg:p-8">
          {contentElements}
        </div>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "space-y-bb border-gray-border z-10 w-full border-b bg-white p-6 lg:p-8",
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("!text-gray-4 !bb-text-display-h4", className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
};
